import { Box, TextField } from '@mui/material'
import { useEffect, useState } from 'react'
import {
  useAddCompanyEmailMutation,
  useAddCompanyPhoneMutation,
  useGetCompanyByIdQuery,
  useGetCompanyEmailsQuery,
  useGetCompanyPhonesQuery,
  useUpdateCompanyByIdMutation,
  useUpdateCompanyEmailMutation,
  useUpdateCompanyPhoneMutation,
  useUpdateUserCarrierAssociationMutation,
} from 'store/company/company.api'
import { CarrierCompany } from 'store/company/company.types'
import { formatPhoneForDisplay } from 'utils/phone'
import {
  DetailField,
  detailTextFieldSx,
} from '../../customer-details/customer-detail-fields'
import {
  findDispatchEmail,
  findDispatchPhone,
  formatCompanyAddress,
  parseCarrierAddress,
  upsertDispatchEmail,
  upsertDispatchPhone,
} from '../carrier-contact.utils'

interface CarrierFormTabProps {
  carrier: CarrierCompany
  onDirtyChange: (dirty: boolean) => void
  saveToken: number
  onSaved: () => void
}

const CarrierFormTab = ({
  carrier,
  onDirtyChange,
  saveToken,
  onSaved,
}: CarrierFormTabProps) => {
  const companyId = carrier.companyId
  const associationId = carrier.associationId

  const { data: company } = useGetCompanyByIdQuery({ id: companyId })
  const { data: emails = [] } = useGetCompanyEmailsQuery({ companyId })
  const { data: phones = [] } = useGetCompanyPhonesQuery({ companyId })

  const [updateCompany, { isLoading: isUpdatingCompany }] =
    useUpdateCompanyByIdMutation()
  const [updateAssociation, { isLoading: isUpdatingAssociation }] =
    useUpdateUserCarrierAssociationMutation()
  const [addEmail] = useAddCompanyEmailMutation()
  const [updateEmail] = useUpdateCompanyEmailMutation()
  const [addPhone] = useAddCompanyPhoneMutation()
  const [updatePhone] = useUpdateCompanyPhoneMutation()

  const [carrierName, setCarrierName] = useState('')
  const [carrierAddress, setCarrierAddress] = useState('')
  const [dispatchName, setDispatchName] = useState('')
  const [dispatchEmail, setDispatchEmail] = useState('')
  const [dispatchPhone, setDispatchPhone] = useState('')
  const [dispatchCell, setDispatchCell] = useState('')
  const [dispatchAfterHours, setDispatchAfterHours] = useState('')

  useEffect(() => {
    if (!company) return
    setCarrierName(company.companyName ?? '')
    setCarrierAddress(formatCompanyAddress(company))
    setDispatchName(carrier.dispatchName ?? '')
    setDispatchEmail(findDispatchEmail(emails)?.email ?? '')
    setDispatchPhone(
      formatPhoneForDisplay(findDispatchPhone(phones, 'Dispatch')?.phone)
    )
    setDispatchCell(
      formatPhoneForDisplay(findDispatchPhone(phones, 'DispatchCell')?.phone)
    )
    setDispatchAfterHours(
      formatPhoneForDisplay(
        findDispatchPhone(phones, 'DispatchAfterHoursPhone')?.phone
      )
    )
  }, [company, carrier.dispatchName, companyId, emails, phones])

  const isDirty = (() => {
    if (!company) return false

    const originalAddress = formatCompanyAddress(company)
    const originalDispatchEmail = findDispatchEmail(emails)?.email ?? ''
    const originalDispatchPhone = formatPhoneForDisplay(
      findDispatchPhone(phones, 'Dispatch')?.phone
    )
    const originalDispatchCell = formatPhoneForDisplay(
      findDispatchPhone(phones, 'DispatchCell')?.phone
    )
    const originalDispatchAfterHours = formatPhoneForDisplay(
      findDispatchPhone(phones, 'DispatchAfterHoursPhone')?.phone
    )

    return (
      carrierName.trim() !== (company.companyName ?? '').trim() ||
      carrierAddress.trim() !== originalAddress.trim() ||
      dispatchName.trim() !== (carrier.dispatchName ?? '').trim() ||
      dispatchEmail.trim() !== originalDispatchEmail.trim() ||
      dispatchPhone.trim() !== originalDispatchPhone.trim() ||
      dispatchCell.trim() !== originalDispatchCell.trim() ||
      dispatchAfterHours.trim() !== originalDispatchAfterHours.trim()
    )
  })()

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    if (!saveToken || !company) return

    const save = async () => {
      try {
        await updateCompany({
          id: companyId,
          companyName: carrierName.trim(),
          ...parseCarrierAddress(carrierAddress),
        }).unwrap()

        await updateAssociation({
          id: associationId,
          dispatchName: dispatchName.trim() || undefined,
        }).unwrap()

        const contactMutations = { addEmail, updateEmail, addPhone, updatePhone }

        await upsertDispatchEmail(
          companyId,
          dispatchEmail,
          emails,
          contactMutations
        )
        await upsertDispatchPhone(
          companyId,
          dispatchPhone,
          'Dispatch',
          phones,
          contactMutations
        )
        await upsertDispatchPhone(
          companyId,
          dispatchCell,
          'DispatchCell',
          phones,
          contactMutations
        )
        await upsertDispatchPhone(
          companyId,
          dispatchAfterHours,
          'DispatchAfterHoursPhone',
          phones,
          contactMutations
        )

        onSaved()
      } catch (error) {
        console.error('Failed to update carrier', error)
      }
    }

    void save()
    // Intentionally keyed on saveToken only — captures form state at save time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveToken])

  const isSaving = isUpdatingCompany || isUpdatingAssociation

  return (
    <Box sx={{ px: 3, py: 2, maxWidth: 640 }}>
      <DetailField label="Carrier Name">
        <TextField
          fullWidth
          size="small"
          value={carrierName}
          onChange={(e) => setCarrierName(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Carrier Address">
        <TextField
          fullWidth
          size="small"
          value={carrierAddress}
          onChange={(e) => setCarrierAddress(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Dispatch Name">
        <TextField
          fullWidth
          size="small"
          value={dispatchName}
          onChange={(e) => setDispatchName(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Dispatch Email">
        <TextField
          fullWidth
          size="small"
          value={dispatchEmail}
          onChange={(e) => setDispatchEmail(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Dispatch Phone">
        <TextField
          fullWidth
          size="small"
          value={dispatchPhone}
          onChange={(e) => setDispatchPhone(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Dispatch Cell">
        <TextField
          fullWidth
          size="small"
          value={dispatchCell}
          onChange={(e) => setDispatchCell(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>

      <DetailField label="Dispatch After Hours">
        <TextField
          fullWidth
          size="small"
          value={dispatchAfterHours}
          onChange={(e) => setDispatchAfterHours(e.target.value)}
          disabled={isSaving}
          sx={detailTextFieldSx}
        />
      </DetailField>
    </Box>
  )
}

export default CarrierFormTab
